import {
  ArrowRight,
  BellRing,
  CalendarCheck2,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock3,
  FileText,
  MessageCircleMore,
  ReceiptText,
  ShieldCheck,
  Sparkles,
  UsersRound,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useI18n } from '../lib/i18n'
import { fetchSubscriptionPlans } from '../lib/subscriptionCatalog'
import { subscriptionPlans, type SubscriptionPlanDefinition } from '../lib/subscriptions'
import { trackMarketingEvent } from '../lib/marketing'
import '../marketing.css'

type MarketingLocale = 'en' | 'es' | 'pt-BR'

const landingPaths: Record<MarketingLocale, string> = { en: '/', es: '/es', 'pt-BR': '/pt' }
const localizedUI = {
  en: { title: 'TidyPing | Cleaning business scheduling software', appStore: 'Download TidyPing on the App Store', approved: 'Company approved', confirmation: 'Client confirmation', clients: 'Clients', messages: 'Messages', finance: 'Finance', date: 'Monday, September 14', secondClient: 'Andrea’s Home' },
  es: { title: 'TidyPing | Agenda para compañías de limpieza', appStore: 'Descargar TidyPing en App Store', approved: 'Empresa aprobó', confirmation: 'Confirmación del cliente', clients: 'Clientes', messages: 'Mensajes', finance: 'Finanzas', date: 'Lunes, 14 de septiembre', secondClient: 'Casa de Andrea' },
  'pt-BR': { title: 'TidyPing | Gestão para empresas de limpeza', appStore: 'Baixar TidyPing na App Store', approved: 'Empresa aprovou', confirmation: 'Confirmação do cliente', clients: 'Clientes', messages: 'Mensagens', finance: 'Financeiro', date: 'Segunda-feira, 14 de setembro', secondClient: 'Casa da Andrea' },
}

const appStoreURL = 'https://apps.apple.com/us/app/tidyping/id6792330231'

const content = {
  en: {
    tagline: 'Cleaning, coordinated.',
    nav: ['Product', 'How it works', 'Pricing'],
    signIn: 'Sign in',
    eyebrow: 'Built for residential cleaning companies',
    title: 'Cleanings confirmed. Clients informed. Your day under control.',
    intro: 'TidyPing puts scheduling, two-sided confirmations, client chat, team assignments, invoices, and business reporting in one calm workspace.',
    trial: 'Start 30-day free trial',
    trialNote: 'No card required. Clients use TidyPing free.',
    badges: ['Two-sided confirmations', 'Real-time client chat', 'iPhone, iPad & web'],
    previewLabel: 'Live product preview',
    today: 'Today',
    jobs: "Today's jobs",
    upcoming: 'Upcoming',
    needsApproval: 'Needs approval',
    confirmed: 'Confirmed',
    jobClient: 'The Miller Home',
    jobTime: '10:00 AM – 12:00 PM',
    status: 'Waiting for client',
    clientView: 'Client view',
    cleaningRequest: 'Confirm your cleaning',
    requestTime: 'Mon, Sep 14 · 10:00 AM',
    confirm: 'Confirm time',
    reschedule: 'Request a change',
    problemEyebrow: 'Stop running the week from text messages',
    problemTitle: 'One source of truth for every cleaning.',
    problemBody: 'No more guessing who confirmed, searching old conversations, or rebuilding the schedule after one change. TidyPing keeps the company and client on the same page.',
    features: [
      ['Mutual scheduling', 'The company approves first. Then the client confirms or suggests available times without breaking the calendar.'],
      ['In-app conversations', 'Keep client messages, photos, and videos beside the relationship—with push notifications and full history.'],
      ['A focused team', 'Assign cleaners and managers while protecting client lists, schedule changes, and financial data by role.'],
      ['Invoices and clarity', 'Create invoices by cleaning or date range and see paid revenue without processing client payments in TidyPing.'],
    ],
    workflowEyebrow: 'How it works',
    workflowTitle: 'From a new job to a confirmed visit in three steps.',
    workflow: [
      ['Schedule it', 'Choose the client, service, team, date, time, and recurrence.'],
      ['Approve together', 'The company approves, then TidyPing asks the client to confirm.'],
      ['Keep everyone updated', 'Changes, messages, completion notices, and invoices stay connected.'],
    ],
    proofEyebrow: 'Designed around real cleaning operations',
    proofTitle: 'The important work is visible before it becomes a problem.',
    proofPoints: ['Saturday or day-before confirmation reminders', 'Only today’s jobs on Today', 'Recurring schedules renew one month at a time', 'Owner-only prices and financial reports'],
    pricingEyebrow: 'Simple plans',
    pricingTitle: 'Start free. Choose a plan when your team is ready.',
    pricingBody: 'Every business gets 30 days free without entering payment information. Subscribe anytime from iPhone or the web.',
    month: '/ month',
    team: 'team members',
    clients: 'active clients',
    unlimited: 'Unlimited',
    popular: 'Most popular',
    choose: 'Start free',
    faqTitle: 'Questions before you start?',
    faq: [
      ['Do clients pay for TidyPing?', 'No. Connected clients use the app free to confirm visits, chat, and access their invoices.'],
      ['Does TidyPing collect cleaning payments?', 'No. TidyPing tracks invoices and payment status, but your cleaning business collects client payments outside the app.'],
      ['Can my employees see prices or every client?', 'No. Cleaners see only assigned work. Only owners can access financial information and sensitive management controls.'],
      ['Can I use it from a computer?', 'Yes. Owners can manage TidyPing from the web, and the native app supports iPhone and iPad.'],
    ],
    finalTitle: 'Spend less time chasing confirmations.',
    finalBody: 'Give your cleaning company a schedule your team and clients can trust.',
    footerProduct: 'Product',
    privacy: 'Privacy',
    terms: 'Terms',
    support: 'Support',
    copyright: '© 2026 Maintech Solutions LLC. All rights reserved.',
  },
  es: {
    tagline: 'Limpieza, coordinada.',
    nav: ['Producto', 'Cómo funciona', 'Precios'],
    signIn: 'Iniciar sesión',
    eyebrow: 'Creado para compañías de limpieza residencial',
    title: 'Limpiezas confirmadas. Clientes informados. Tu día bajo control.',
    intro: 'TidyPing reúne agenda, confirmaciones de ambas partes, chat con clientes, asignación del equipo, facturas y reportes en un solo lugar.',
    trial: 'Prueba gratis por 30 días',
    trialNote: 'Sin tarjeta. Los clientes usan TidyPing gratis.',
    badges: ['Confirmación de ambas partes', 'Chat con clientes en tiempo real', 'iPhone, iPad y web'],
    previewLabel: 'Vista previa del producto',
    today: 'Hoy',
    jobs: 'Trabajos de hoy',
    upcoming: 'Próximos',
    needsApproval: 'Por aprobar',
    confirmed: 'Confirmados',
    jobClient: 'Casa de la familia Miller',
    jobTime: '10:00 AM – 12:00 PM',
    status: 'Esperando al cliente',
    clientView: 'Vista del cliente',
    cleaningRequest: 'Confirma tu limpieza',
    requestTime: 'Lun, 14 de sep · 10:00 AM',
    confirm: 'Confirmar horario',
    reschedule: 'Pedir un cambio',
    problemEyebrow: 'Deja de organizar la semana por mensajes de texto',
    problemTitle: 'Una sola fuente de información para cada limpieza.',
    problemBody: 'No más dudas sobre quién confirmó, búsquedas en conversaciones viejas ni rehacer toda la agenda por un cambio. TidyPing mantiene a la empresa y al cliente coordinados.',
    features: [
      ['Agenda de común acuerdo', 'La empresa aprueba primero. Después el cliente confirma o propone horarios disponibles sin desordenar el calendario.'],
      ['Conversaciones dentro de la app', 'Guarda mensajes, fotos y videos con el cliente, con notificaciones de la app e historial completo.'],
      ['Un equipo enfocado', 'Asigna personal de limpieza y supervisores mientras proteges la lista de clientes, los cambios de agenda y la información financiera según el rol.'],
      ['Facturas y claridad', 'Crea facturas por limpieza o período y consulta ingresos cobrados sin procesar pagos de clientes en TidyPing.'],
    ],
    workflowEyebrow: 'Cómo funciona',
    workflowTitle: 'De un trabajo nuevo a una visita confirmada en tres pasos.',
    workflow: [
      ['Programa la limpieza', 'Elige cliente, servicio, equipo, fecha, hora y frecuencia.'],
      ['Confirmen juntos', 'La empresa aprueba y después TidyPing solicita la confirmación del cliente.'],
      ['Mantén a todos informados', 'Cambios, mensajes, avisos de finalización y facturas permanecen conectados.'],
    ],
    proofEyebrow: 'Diseñado para operaciones reales de limpieza',
    proofTitle: 'Lo importante aparece antes de convertirse en un problema.',
    proofPoints: ['Recordatorios el sábado o el día anterior', 'Solo los trabajos del día en Hoy', 'Agenda recurrente renovada un mes a la vez', 'Precios y reportes financieros solo para el dueño'],
    pricingEyebrow: 'Planes sencillos',
    pricingTitle: 'Empieza gratis. Elige un plan cuando tu equipo esté listo.',
    pricingBody: 'Cada empresa recibe 30 días gratis sin registrar una forma de pago. Suscríbete cuando quieras desde iPhone o la web.',
    month: '/ mes',
    team: 'miembros del equipo',
    clients: 'clientes activos',
    unlimited: 'Ilimitados',
    popular: 'Más popular',
    choose: 'Empezar gratis',
    faqTitle: '¿Preguntas antes de empezar?',
    faq: [
      ['¿Los clientes pagan por TidyPing?', 'No. Los clientes conectados usan la app gratis para confirmar visitas, conversar y acceder a sus facturas.'],
      ['¿TidyPing cobra los pagos de las limpiezas?', 'No. TidyPing controla facturas y estados de pago, pero tu empresa cobra al cliente fuera de la app.'],
      ['¿Mis empleados pueden ver precios o todos los clientes?', 'No. El personal de limpieza ve solamente el trabajo asignado. Solo los dueños acceden a finanzas y controles administrativos sensibles.'],
      ['¿Puedo usarlo desde una computadora?', 'Sí. Los dueños pueden administrar TidyPing desde la web, y la app nativa funciona en iPhone y iPad.'],
    ],
    finalTitle: 'Pierde menos tiempo buscando confirmaciones.',
    finalBody: 'Dale a tu compañía una agenda en la que tu equipo y tus clientes puedan confiar.',
    footerProduct: 'Producto',
    privacy: 'Privacidad',
    terms: 'Términos',
    support: 'Soporte',
    copyright: '© 2026 Maintech Solutions LLC. Todos los derechos reservados.',
  },
  'pt-BR': {
    tagline: 'Limpeza, organizada.',
    nav: ['Produto', 'Como funciona', 'Preços'],
    signIn: 'Entrar',
    eyebrow: 'Feito para empresas de limpeza residencial',
    title: 'Limpezas confirmadas. Clientes informados. Seu dia sob controle.',
    intro: 'O TidyPing reúne agenda, confirmações da empresa e do cliente, conversas, distribuição da equipe, faturas e relatórios em um só lugar.',
    trial: 'Teste grátis por 30 dias',
    trialNote: 'Sem cartão. Clientes usam o TidyPing de graça.',
    badges: ['Confirmação dos dois lados', 'Chat com clientes em tempo real', 'iPhone, iPad e web'],
    previewLabel: 'Prévia do produto',
    today: 'Hoje',
    jobs: 'Serviços de hoje',
    upcoming: 'Próximos',
    needsApproval: 'A aprovar',
    confirmed: 'Confirmados',
    jobClient: 'Casa da família Miller',
    jobTime: '10:00 AM – 12:00 PM',
    status: 'Aguardando cliente',
    clientView: 'Visão do cliente',
    cleaningRequest: 'Confirme sua limpeza',
    requestTime: 'Seg, 14 de set · 10:00 AM',
    confirm: 'Confirmar horário',
    reschedule: 'Pedir alteração',
    problemEyebrow: 'Pare de organizar a semana por mensagens soltas',
    problemTitle: 'Todas as informações de cada limpeza no mesmo lugar.',
    problemBody: 'Chega de procurar quem confirmou, vasculhar conversas antigas ou refazer a agenda a cada mudança. O TidyPing mantém sua empresa e seus clientes alinhados.',
    features: [
      ['Agenda combinada', 'A empresa aprova primeiro. Depois, o cliente confirma ou sugere horários disponíveis sem bagunçar o calendário.'],
      ['Conversas dentro do app', 'Mantenha mensagens, fotos e vídeos junto ao histórico de cada cliente, com notificações no aplicativo.'],
      ['Equipe organizada', 'Distribua os serviços entre profissionais e gerentes. Cada função tem seu próprio acesso a clientes, agenda e informações financeiras.'],
      ['Faturas e controle', 'Crie faturas por limpeza ou período e acompanhe os valores recebidos. Os pagamentos dos clientes são feitos fora do TidyPing.'],
    ],
    workflowEyebrow: 'Como funciona',
    workflowTitle: 'Do novo serviço à visita confirmada em três passos.',
    workflow: [
      ['Agende a limpeza', 'Escolha cliente, serviço, equipe, data, horário e frequência.'],
      ['Confirmem juntos', 'A empresa aprova e o TidyPing solicita a confirmação do cliente.'],
      ['Mantenha todos informados', 'Alterações, mensagens, avisos de conclusão e faturas ficam conectados.'],
    ],
    proofEyebrow: 'Pensado para a rotina de quem trabalha com limpeza',
    proofTitle: 'Veja o que precisa de atenção antes de virar um problema.',
    proofPoints: ['Lembretes no sábado ou no dia anterior à visita', 'Somente os serviços do dia na tela Hoje', 'Agendamentos recorrentes renovados mês a mês', 'Preços e relatórios financeiros só para o dono'],
    pricingEyebrow: 'Planos simples',
    pricingTitle: 'Comece grátis. Escolha um plano quando estiver pronto.',
    pricingBody: 'Toda empresa tem 30 dias grátis, sem cadastrar cartão. Assine quando quiser pelo iPhone ou pela web. Os preços são em dólares americanos.',
    month: '/ mês',
    team: 'pessoas na equipe',
    clients: 'clientes ativos',
    unlimited: 'Ilimitados',
    popular: 'Mais popular',
    choose: 'Começar grátis',
    faqTitle: 'Dúvidas antes de começar?',
    faq: [
      ['Os clientes pagam pelo TidyPing?', 'Não. Os clientes conectados usam o app de graça para confirmar visitas, conversar e acessar suas faturas.'],
      ['O TidyPing cobra os pagamentos das limpezas?', 'Não. O TidyPing organiza faturas e o status dos pagamentos. Sua empresa recebe dos clientes fora do app e registra os valores recebidos.'],
      ['Minha equipe pode ver preços ou todos os clientes?', 'Não. Os profissionais de limpeza veem apenas os serviços atribuídos a eles. Somente o dono acessa as informações financeiras e os controles administrativos restritos.'],
      ['Posso usar pelo computador?', 'Sim. Você pode administrar sua empresa pela web, e o aplicativo nativo funciona no iPhone e no iPad.'],
    ],
    finalTitle: 'Menos tempo correndo atrás de confirmações.',
    finalBody: 'Dê à sua empresa uma agenda em que sua equipe e seus clientes possam confiar.',
    footerProduct: 'Produto',
    privacy: 'Privacidade',
    terms: 'Termos',
    support: 'Suporte',
    copyright: '© 2026 Maintech Solutions LLC. Todos os direitos reservados.',
  },
} as const

const featureIcons = [CalendarCheck2, MessageCircleMore, UsersRound, ReceiptText]

export function LandingPage({ locale }: { locale: MarketingLocale }) {
  const copy = content[locale]
  const ui = localizedUI[locale]
  const navigate = useNavigate()
  const { setLocale } = useI18n()
  const [plans, setPlans] = useState<SubscriptionPlanDefinition[]>(subscriptionPlans)

  useEffect(() => {
    void fetchSubscriptionPlans(locale).then(setPlans).catch(() => setPlans(subscriptionPlans))
  }, [locale])

  useEffect(() => {
    const title = ui.title
    const description = copy.intro
    const canonical = `https://tidyping.com${landingPaths[locale] === '/' ? '' : landingPaths[locale]}`
    document.documentElement.lang = locale
    document.title = title
    setMeta('description', description)
    setMeta('og:title', title, 'property')
    setMeta('og:description', description, 'property')
    setMeta('og:type', 'website', 'property')
    setMeta('og:url', canonical, 'property')
    setLink('canonical', canonical)
    setLink('alternate', 'https://tidyping.com/', 'en')
    setLink('alternate', 'https://tidyping.com/es', 'es')
    setLink('alternate', 'https://tidyping.com/pt', 'pt-BR')
    trackMarketingEvent('landing_view', locale)
  }, [copy.intro, locale, ui.title])

  function track(event: 'app_store_click' | 'web_sign_in_click', surface: string) {
    if (event === 'web_sign_in_click') setLocale(locale)
    trackMarketingEvent(event, locale, { surface })
  }

  return (
    <div className="marketing-page">
      <header className="marketing-header">
        <a className="marketing-brand" href={landingPaths[locale]} aria-label="TidyPing home">
          <img src="/brand-mark.png" alt="" />
          <span><strong>TidyPing</strong><small>{copy.tagline}</small></span>
        </a>
        <nav aria-label="Primary navigation">
          <a href="#product">{copy.nav[0]}</a>
          <a href="#how-it-works">{copy.nav[1]}</a>
          <a href="#pricing">{copy.nav[2]}</a>
        </nav>
        <div className="marketing-header__actions">
          <select className="marketing-language" aria-label="Language / Idioma" value={locale} onChange={(event) => {
            const next = event.target.value as MarketingLocale
            trackMarketingEvent('language_switch', locale, { to: next })
            navigate(`${landingPaths[next]}${window.location.search}`)
          }}><option value="en">EN</option><option value="es">ES</option><option value="pt-BR">PT</option></select>
          <Link className="marketing-signin" to="/signin" onClick={() => track('web_sign_in_click', 'header')}>{copy.signIn}</Link>
          <Link className="marketing-button marketing-button--small" to="/signin" onClick={() => track('web_sign_in_click', 'header-trial')}>{copy.trial}<ArrowRight size={16} /></Link>
        </div>
      </header>

      <main>
        <section className="marketing-hero">
          <div className="marketing-hero__copy">
            <span className="marketing-kicker"><Sparkles size={15} />{copy.eyebrow}</span>
            <h1>{copy.title}</h1>
            <p>{copy.intro}</p>
            <div className="marketing-hero__actions">
              <Link className="marketing-button" to="/signin" onClick={() => track('web_sign_in_click', 'hero')}>{copy.trial}<ArrowRight size={18} /></Link>
              <a className="marketing-app-store" href={appStoreURL} target="_blank" rel="noreferrer" onClick={() => track('app_store_click', 'hero')}>
                <img src="/download-on-the-app-store.svg" alt={ui.appStore} />
              </a>
            </div>
            <small className="marketing-trial-note"><ShieldCheck size={15} />{copy.trialNote}</small>
            <div className="marketing-badges">{copy.badges.map((badge) => <span key={badge}><Check size={14} />{badge}</span>)}</div>
          </div>
          <ProductPreview locale={locale} copy={copy} />
        </section>

        <section id="product" className="marketing-section marketing-product">
          <div className="marketing-section__heading">
            <span className="marketing-eyebrow">{copy.problemEyebrow}</span>
            <h2>{copy.problemTitle}</h2>
            <p>{copy.problemBody}</p>
          </div>
          <div className="marketing-feature-grid">
            {copy.features.map(([title, description], index) => {
              const Icon = featureIcons[index] ?? CalendarCheck2
              return <article key={title}><span><Icon size={23} /></span><h3>{title}</h3><p>{description}</p></article>
            })}
          </div>
        </section>

        <section id="how-it-works" className="marketing-section marketing-workflow">
          <div className="marketing-section__heading">
            <span className="marketing-eyebrow">{copy.workflowEyebrow}</span>
            <h2>{copy.workflowTitle}</h2>
          </div>
          <div className="marketing-workflow__steps">
            {copy.workflow.map(([title, description], index) => <article key={title}><span>{index + 1}</span><div><h3>{title}</h3><p>{description}</p></div>{index < 2 && <ChevronRight aria-hidden="true" />}</article>)}
          </div>
        </section>

        <section className="marketing-proof">
          <div>
            <span className="marketing-eyebrow">{copy.proofEyebrow}</span>
            <h2>{copy.proofTitle}</h2>
            <ul>{copy.proofPoints.map((point) => <li key={point}><CheckCircle2 size={19} />{point}</li>)}</ul>
          </div>
          <div className="marketing-confirmation-card" aria-label="TidyPing confirmation workflow preview">
            <header><span><BellRing size={19} /></span><div><strong>{copy.cleaningRequest}</strong><small>{copy.requestTime}</small></div></header>
            <div className="marketing-confirmation-flow"><span className="complete"><Check size={13} /> <b>{ui.approved}</b></span><i /><span><Clock3 size={13} /> <b>{ui.confirmation}</b></span></div>
            <button>{copy.confirm}</button><button className="secondary">{copy.reschedule}</button>
          </div>
        </section>

        <section id="pricing" className="marketing-section marketing-pricing">
          <div className="marketing-section__heading">
            <span className="marketing-eyebrow">{copy.pricingEyebrow}</span>
            <h2>{copy.pricingTitle}</h2>
            <p>{copy.pricingBody}</p>
          </div>
          <div className="marketing-plan-grid">
            {plans.map((plan) => <article className={plan.recommended ? 'recommended' : ''} key={plan.tier}>
              {plan.recommended && <span className="marketing-popular">{copy.popular}</span>}
              <h3>{capitalize(plan.tier)}</h3>
              <p>{plan.subtitle}</p>
              <strong>{formatUSD(plan.monthlyPriceCents, locale)}<small>{copy.month}</small></strong>
              <div className="marketing-limits"><span><UsersRound size={17} />{plan.teamMemberLimit} {copy.team}</span><span><FileText size={17} />{plan.clientLimit ?? copy.unlimited} {copy.clients}</span></div>
              <ul>{plan.features.map((feature) => <li key={feature}><Check size={14} />{feature}</li>)}</ul>
              <Link to="/signin" onClick={() => track('web_sign_in_click', `pricing-${plan.tier}`)}>{copy.choose}<ArrowRight size={16} /></Link>
            </article>)}
          </div>
        </section>

        <section className="marketing-section marketing-faq">
          <h2>{copy.faqTitle}</h2>
          <div>{copy.faq.map(([question, answer]) => <details key={question}><summary>{question}<span>+</span></summary><p>{answer}</p></details>)}</div>
        </section>

        <section className="marketing-final-cta">
          <div><span className="marketing-eyebrow">TidyPing</span><h2>{copy.finalTitle}</h2><p>{copy.finalBody}</p></div>
          <div><Link className="marketing-button" to="/signin" onClick={() => track('web_sign_in_click', 'final')}>{copy.trial}<ArrowRight size={18} /></Link><a className="marketing-app-store" href={appStoreURL} target="_blank" rel="noreferrer" onClick={() => track('app_store_click', 'final')}><img src="/download-on-the-app-store.svg" alt={ui.appStore} /></a></div>
        </section>
      </main>

      <footer className="marketing-footer">
        <a className="marketing-brand" href={landingPaths[locale]}><img src="/brand-mark.png" alt="" /><span><strong>TidyPing</strong><small>{copy.tagline}</small></span></a>
        <nav><a href="#product">{copy.footerProduct}</a><Link to="/legal/privacy">{copy.privacy}</Link><Link to="/legal/terms">{copy.terms}</Link><Link to="/support">{copy.support}</Link></nav>
        <small>{copy.copyright}</small>
      </footer>
    </div>
  )
}

function ProductPreview({ locale, copy }: { locale: MarketingLocale; copy: typeof content[MarketingLocale] }) {
  const ui = localizedUI[locale]
  return <div className="marketing-preview" aria-label={copy.previewLabel}>
    <span className="marketing-preview__label"><span />{copy.previewLabel}</span>
    <div className="marketing-browser">
      <header><i /><i /><i /><span>tidyping.com</span></header>
      <div className="marketing-browser__body">
        <aside><img src="/brand-mark.png" alt="" /><span className="active"><CalendarCheck2 />{copy.today}</span><span><UsersRound />{ui.clients}</span><span><MessageCircleMore />{ui.messages}</span><span><ReceiptText />{ui.finance}</span></aside>
        <section><small>{ui.date}</small><h3>{copy.today}</h3><div className="marketing-preview__metrics"><span><b>6</b>{copy.upcoming}</span><span><b>2</b>{copy.needsApproval}</span><span><b>4</b>{copy.confirmed}</span></div><h4>{copy.jobs}</h4><article><time>10:00</time><i /><div><strong>{copy.jobClient}</strong><small>{copy.jobTime}</small></div><em>{copy.status}</em></article><article><time>1:30</time><i className="confirmed" /><div><strong>{ui.secondClient}</strong><small>1:30 PM – 3:30 PM</small></div><em className="confirmed">{copy.confirmed}</em></article></section>
      </div>
    </div>
    <div className="marketing-phone">
      <div className="marketing-phone__speaker" />
      <header><img src="/brand-mark.png" alt="" /><span><small>{copy.clientView}</small><strong>TidyPing</strong></span><BellRing /></header>
      <div className="marketing-phone__card"><span><CalendarCheck2 /></span><small>{copy.cleaningRequest}</small><strong>{copy.requestTime}</strong><button>{copy.confirm}</button><button className="secondary">{copy.reschedule}</button></div>
      <nav><CalendarCheck2 /><MessageCircleMore /><span><UsersRound /></span></nav>
    </div>
  </div>
}

function setMeta(key: string, contentValue: string, attribute: 'name' | 'property' = 'name') {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`)
  if (!element) { element = document.createElement('meta'); element.setAttribute(attribute, key); document.head.append(element) }
  element.content = contentValue
}

function setLink(rel: string, href: string, hrefLang?: string) {
  const selector = hrefLang ? `link[rel="${rel}"][hreflang="${hrefLang}"]` : `link[rel="${rel}"]:not([hreflang])`
  let element = document.head.querySelector<HTMLLinkElement>(selector)
  if (!element) { element = document.createElement('link'); element.rel = rel; if (hrefLang) element.hreflang = hrefLang; document.head.append(element) }
  element.href = href
}

function formatUSD(cents: number, locale: MarketingLocale) {
  return new Intl.NumberFormat(locale === 'pt-BR' ? 'pt-BR' : locale === 'es' ? 'es-US' : 'en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(cents / 100)
}

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}
